import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { createLead, fetchLeads, startTrial, updateLead } from "@/lib/platform-api";

const STAGES = ["new", "contacted", "qualified", "trial", "won", "lost"];

export default function PlatformLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [form, setForm] = useState({ email: "", name: "", company: "", source: "manual", notes: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLeads(filter || undefined, sourceFilter || undefined);
      setLeads(data.leads || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, sourceFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const addLead = async (e) => {
    e.preventDefault();
    try {
      await createLead(form);
      toast.success("Lead added");
      setForm({ email: "", name: "", company: "", source: "manual", notes: "" });
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const changeStage = async (leadId, stage) => {
    try {
      await updateLead(leadId, { stage });
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const convertToTrial = async (lead) => {
    try {
      const result = await startTrial({
        ownerEmail: lead.email,
        orgName: lead.company || lead.name || "",
        ownerName: lead.name || "",
        leadId: lead.id,
        notes: lead.notes || "",
      });
      toast.success("Trial created", { description: result.tenantId });
      if (result.temporaryPassword) {
        toast.message("Temporary password", { description: result.temporaryPassword });
      }
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1200px] mx-auto">
      <div className="text-overline">Platform</div>
      <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Leads & inbound</h1>
      <p className="mt-2 text-sm text-[var(--text-secondary)] max-w-2xl">
        Demo and sales requests from the marketing site appear here with source <code className="text-xs">demo</code> or{" "}
        <code className="text-xs">sales</code>.
      </p>

      <form onSubmit={addLead} className="surface p-6 mt-8 grid sm:grid-cols-2 gap-4 max-w-3xl">
        <input
          className="input-dark"
          placeholder="Email *"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
        />
        <input
          className="input-dark"
          placeholder="Name"
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
        />
        <input
          className="input-dark"
          placeholder="Company"
          value={form.company}
          onChange={(e) => setForm((p) => ({ ...p, company: e.target.value }))}
        />
        <select className="input-dark" value={form.source} onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))}>
          <option value="manual">Manual</option>
          <option value="landing">Landing</option>
          <option value="referral">Referral</option>
          <option value="outbound">Outbound</option>
          <option value="demo">Demo request</option>
          <option value="sales">Sales inquiry</option>
        </select>
        <textarea
          className="input-dark sm:col-span-2 min-h-[72px]"
          placeholder="Notes"
          value={form.notes}
          onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
        />
        <button type="submit" className="btn-primary sm:col-span-2 w-fit">
          Add lead
        </button>
      </form>

      <div className="mt-8 flex gap-2 flex-wrap items-center">
        <span className="text-xs text-[var(--text-tertiary)] mr-2">Source</span>
        {["", "demo", "sales"].map((s) => (
          <button
            key={s || "all-src"}
            type="button"
            className={`btn-ghost text-xs ${sourceFilter === s ? "opacity-100" : "opacity-50"}`}
            onClick={() => setSourceFilter(s)}
          >
            {s || "All inbound"}
          </button>
        ))}
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        <button type="button" className={`btn-ghost text-xs ${!filter ? "opacity-100" : "opacity-50"}`} onClick={() => setFilter("")}>
          All stages
        </button>
        {STAGES.map((s) => (
          <button
            key={s}
            type="button"
            className={`btn-ghost text-xs capitalize ${filter === s ? "opacity-100" : "opacity-50"}`}
            onClick={() => setFilter(s)}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          <p className="text-[var(--text-tertiary)]">Loading…</p>
        ) : (
          leads.map((lead) => (
            <div key={lead.id} className="surface p-5 flex flex-wrap gap-4 justify-between items-start">
              <div>
                <div className="font-medium flex items-center gap-2">
                  {lead.email}
                  <span
                    className={`text-[10px] font-mono px-1.5 py-0.5 ${
                      (lead.score || 0) >= 60 ? "text-[var(--primary)]" : "text-[var(--text-tertiary)]"
                    }`}
                  >
                    {lead.score ?? 0}
                  </span>
                </div>
                <div className="text-sm text-[var(--text-secondary)]">
                  {[lead.name, lead.company].filter(Boolean).join(" · ") || "—"}
                </div>
                <div className="text-xs text-[var(--text-tertiary)] mt-1 flex flex-wrap items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded font-mono uppercase tracking-wider ${
                      lead.source === "demo" || lead.source === "sales"
                        ? "bg-[var(--primary)]/15 text-[var(--primary)]"
                        : ""
                    }`}
                  >
                    {lead.source}
                  </span>
                  <span>{lead.orgId ? `org ${lead.orgId}` : "no workspace"}</span>
                </div>
                {lead.notes ? (
                  <p className="text-sm text-[var(--text-secondary)] mt-2 whitespace-pre-wrap">{lead.notes}</p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  className="input-dark text-xs"
                  value={lead.stage}
                  onChange={(e) => changeStage(lead.id, e.target.value)}
                >
                  {STAGES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {lead.stage !== "trial" && lead.stage !== "won" && !lead.orgId ? (
                  <button type="button" className="btn-secondary text-xs" onClick={() => convertToTrial(lead)}>
                    Start trial
                  </button>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
